import { useAlert } from "@/app/contexts/AlertContext"
import { TestSuites } from "@/app/features/reports/types/test-specification"
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog"
import { TestFormValues } from "@app.raytd.com/common/types/test-form"
import { Assessment, AssetEntity } from "@app.raytd.com/store"
import { VisuallyHidden } from "@ariakit/react"
import { Loader2 } from "lucide-react"
import { forwardRef, useCallback, useRef, useState } from "react"
import { TestItem } from 'store/src/lib/tests/entity'
import { TestEditForm } from "./test-edit-form"

type TestEditDialogProps = {
    isOpen: boolean;
    onClose: () => void;
    testItem: TestItem | null;
    isLoading: boolean;
    onSubmit?: (data: any) => void;
    inspections: Assessment[];
    assets: AssetEntity[];
    testSuites: TestSuites;
    initialData?: Partial<TestFormValues>;
    defaultInspectionId?: number;
    onCustomFieldOptionAdded?: (testSuiteId: number, fieldId: string, value: string) => Promise<{ success: boolean, field_id: string, data: any }>;
}

const TestEditDialog = forwardRef(({
    isOpen,
    onClose,
    onSubmit,
    testItem,
    isLoading,
    inspections,
    assets,
    testSuites,
    onCustomFieldOptionAdded,
    initialData,
    defaultInspectionId
}: TestEditDialogProps, ref) => {

    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const { showAlertDialog } = useAlert();
    const formRef = useRef<any>(null);

    const handleDirtyChange = useCallback((isDirty: boolean) => {
        setHasUnsavedChanges(isDirty);
    }, []);

    const handleOpenChange = useCallback((newOpen: boolean) => {
        if (!newOpen) {
            if (hasUnsavedChanges) {
                showAlertDialog({
                    title: 'Unsaved Changes',
                    description: 'You have unsaved changes. Are you sure you want to leave?',
                    confirmText: 'Leave',
                    cancelText: 'Stay',
                }).then((confirmed) => {
                    if (confirmed) {
                        setHasUnsavedChanges(false);
                        onClose();
                    }
                });
            } else {
                onClose();
            }
        }
    }, [onClose, showAlertDialog, hasUnsavedChanges]);

    const handleSubmit = useCallback((data: any) => {
        onSubmit?.(data);
        onClose();
    }, [onSubmit, onClose]);

    return (
        <Dialog open={isOpen} onOpenChange={handleOpenChange}>
            <VisuallyHidden>
                <DialogTitle>Test Edit</DialogTitle>
                <DialogDescription>Edit the test for the asset.</DialogDescription>
            </VisuallyHidden>
            <DialogContent
                className="max-w-2xl z-50 h-[90vh] flex flex-col py-6 px-2 bg-white"
                onOpenAutoFocus={(event) => event.preventDefault()}
                onEscapeKeyDown={(event) => event.preventDefault()}
                onPointerDownOutside={(event) => event.preventDefault()}
            >           {isLoading ? (
                <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-4 h-4 animate-spin" />
                </div>
            ) : (
                <TestEditForm
                    ref={formRef}
                    key={testItem?.assessmentTestId != null ? `test-form-${testItem.assessmentTestId}` : 'test-form-new'}
                    testItem={testItem}
                    isLoading={isLoading}
                    inspections={inspections}
                    assets={assets}
                    testSuites={testSuites as any}
                    onDirtyChange={handleDirtyChange}
                    onSubmit={handleSubmit}
                    onCustomFieldOptionAdded={onCustomFieldOptionAdded}
                    initialData={initialData}
                    defaultInspectionId={defaultInspectionId}
                />
            )}
            </DialogContent>
        </Dialog>
    )
})

export default TestEditDialog;